import { useEffect, useReducer } from 'react';
import { useAuthStore } from '@/store/auth';
import { ticketRecoveryOwner, TICKET_RECOVERY_EVENT } from '@/utils/ticketRecovery';
import { memoryOnlyTicketEditsText } from '@/utils/ticketSessionSnapshot';
import { MEMORY_TICKET_SAVES_EVENT } from '@/utils/offlineQueue';
import { TICKET_RETAINED_EVENT } from '@/utils/ticketRetainedDrafts';
import { showToast } from '@/components/Toast';

/** Survives auth expiry in this tab; only the original owner can see/copy it. */
export function MemoryTicketSaveNotice() {
  const owner = useAuthStore((state) => ticketRecoveryOwner(state.user));
  const [, refresh] = useReducer((value: number) => value + 1, 0);
  useEffect(() => {
    window.addEventListener(MEMORY_TICKET_SAVES_EVENT, refresh);
    window.addEventListener(TICKET_RECOVERY_EVENT, refresh);
    window.addEventListener(TICKET_RETAINED_EVENT, refresh);
    return () => {
      window.removeEventListener(MEMORY_TICKET_SAVES_EVENT, refresh);
      window.removeEventListener(TICKET_RECOVERY_EVENT, refresh);
      window.removeEventListener(TICKET_RETAINED_EVENT, refresh);
    };
  }, []);
  const text = memoryOnlyTicketEditsText(owner);
  if (!text) return null;
  async function copyEdits() {
    if (ticketRecoveryOwner(useAuthStore.getState().user) !== owner) return;
    try {
      await navigator.clipboard.writeText(memoryOnlyTicketEditsText(owner));
      showToast('Edits copied.', 'success');
    } catch {
      showToast('Could not copy edits. Copy them from the ticket before closing this tab.', 'error');
    }
  }
  return (
    <aside role="status" aria-label="Edits kept only in this tab" className="ft-card">
      <p>Some unsent ticket edits could not be stored. They are kept only in this tab; copy them before closing or reloading it.</p>
      <button type="button" className="btn btn-secondary" onClick={() => { void copyEdits(); }}>Copy edits</button>
    </aside>
  );
}
